import React, { useEffect, useState } from "react"
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    IconButton,
    InputAdornment,
    TextField,
    useMediaQuery,
    useTheme
} from "@mui/material"
import { Close, Search } from "@mui/icons-material"

/**
 * Picks the teachers of one subject. Teachers are stored on the subject as
 * "First Last" name strings, as the older subject screen saved them.
 */
export default function AssignTeachersDialog({
    open = false,
    onClose,
    onSave,
    subjectName = "",
    teachers = [],
    initialSelected = [],
    loading = false,
    saving = false
}) {
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"))
    const [selected, setSelected] = useState([])
    const [search, setSearch] = useState("")

    useEffect(() => {
        if (open) {
            setSelected(initialSelected.filter((name) => typeof name === "string"))
            setSearch("")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const names = teachers.map((user) => `${user.firstname || ""} ${user.lastname || ""}`.trim()).filter(Boolean)
    // Keep teachers already on the subject visible even if they left the staff list
    const options = Array.from(new Set([...names, ...selected]))
    const term = search.trim().toLowerCase()
    const visible = options.filter((name) => name.toLowerCase().includes(term))

    const toggle = (name) =>
        setSelected((prev) => (prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]))

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="xs"
            aria-labelledby="teachers-dialog-title"
            slotProps={{ paper: { sx: { borderRadius: fullScreen ? 0 : "16px" } } }}
        >
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
                <div className="min-w-0">
                    <h2 id="teachers-dialog-title" className="text-lg font-semibold text-slate-900">
                        Assign teachers
                    </h2>
                    <p className="mt-0.5 truncate text-sm text-slate-500">
                        {subjectName} · {selected.length} selected
                    </p>
                </div>
                <IconButton aria-label="Close" onClick={onClose} disabled={saving} edge="end">
                    <Close />
                </IconButton>
            </div>

            <div className="border-b border-slate-100 px-5 py-3">
                <TextField
                    fullWidth
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search teachers"
                    inputProps={{ "aria-label": "Search teachers" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
            </div>

            <div className="min-h-[200px] flex-1 overflow-y-auto sm:max-h-[360px]">
                {loading ? (
                    <div className="flex justify-center py-10">
                        <CircularProgress size={24} />
                    </div>
                ) : options.length === 0 ? (
                    <p className="px-5 py-10 text-center text-sm text-slate-500">
                        No staff members with the Teacher designation yet.
                    </p>
                ) : visible.length === 0 ? (
                    <p className="px-5 py-10 text-center text-sm text-slate-500">No teachers match.</p>
                ) : (
                    <ul className="py-1">
                        {visible.map((name) => (
                            <li key={name}>
                                <label className="flex cursor-pointer items-center gap-2 px-3 py-1 hover:bg-slate-50">
                                    <Checkbox size="small" checked={selected.includes(name)} onChange={() => toggle(name)} />
                                    <span className="text-sm text-slate-800">{name}</span>
                                </label>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 px-5 py-3 sm:flex-row sm:justify-end">
                <Button onClick={onClose} disabled={saving} color="inherit" sx={{ textTransform: "none", borderRadius: "10px" }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    disabled={saving || loading}
                    onClick={() => onSave(selected)}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ textTransform: "none", borderRadius: "10px", px: 3 }}
                >
                    {saving ? "Saving…" : "Save teachers"}
                </Button>
            </div>
        </Dialog>
    )
}
